import React, { useRef, useState } from "react";
import { ShieldCheck, Upload, FileText, X } from "lucide-react";

const ACCEPT = "image/jpeg,image/png,image/webp,application/pdf";
const OK = ["image/jpeg", "image/png", "image/webp", "application/pdf"];

// Controlled photo-ID picker with preview + security notice.
// Props: file, onFile(file|null), notice (bool), compact (bool)
export function IdUpload({ file, onFile, notice = true, compact = false }) {
  const ref = useRef(null);
  const [preview, setPreview] = useState(null);

  const pick = (f) => {
    if (!f) return;
    if (!OK.includes(f.type)) { alert("Please upload a JPG, PNG, WEBP image or a PDF."); return; }
    if (f.size > 10 * 1024 * 1024) { alert("File too large. Maximum size is 10MB."); return; }
    onFile(f);
    if (f.type.startsWith("image/")) {
      const r = new FileReader();
      r.onload = (e) => setPreview(e.target.result);
      r.readAsDataURL(f);
    } else setPreview(null);
  };

  const clear = () => { onFile(null); setPreview(null); if (ref.current) ref.current.value = ""; };

  return (
    <div data-testid="id-upload">
      {notice && (
        <div data-testid="id-security-notice" className="rounded-xl bg-forest/5 border border-forest/15 px-4 py-3 mb-4">
          <div className="flex items-center gap-2 text-forest font-semibold text-sm"><ShieldCheck size={16} className="text-gold" /> Security Verification Required</div>
          <p className="text-xs text-stone-500 mt-1 leading-relaxed">For the safety and security of our guests and property, all reservations require a valid photo ID to be uploaded during booking. Your identification will be securely stored and used solely for identity verification purposes.</p>
          <p className="text-[11px] text-stone-400 mt-1">Accepted: Passport, Driving Licence, National ID or Residence Permit — JPG, PNG, WEBP or PDF.</p>
        </div>
      )}

      {!file ? (
        <button type="button" onClick={() => ref.current?.click()} data-testid="id-upload-trigger"
          className="w-full rounded-xl border-2 border-dashed border-stone-300 hover:border-rose bg-sand/40 hover:bg-rose/5 transition-colors py-6 flex flex-col items-center gap-2 text-stone-500">
          <Upload size={22} className="text-rose" />
          <span className="text-sm font-medium text-forest">Upload photo ID <span className="text-rose">*</span></span>
          <span className="text-xs">Tap to choose an image or PDF</span>
        </button>
      ) : (
        <div className="rounded-xl border border-stone-200 p-3 flex items-center gap-3" data-testid="id-upload-preview">
          {preview ? (
            <img src={preview} alt="ID preview" className={`${compact ? "h-14 w-14" : "h-20 w-20"} object-cover rounded-lg`} />
          ) : (
            <div className={`${compact ? "h-14 w-14" : "h-20 w-20"} rounded-lg bg-forest/10 flex items-center justify-center text-forest`}><FileText size={26} /></div>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-forest truncate">{file.name}</div>
            <div className="text-xs text-stone-400">{(file.size / 1024).toFixed(0)} KB · ready to submit</div>
            <button type="button" onClick={() => ref.current?.click()} data-testid="id-upload-replace" className="text-xs text-rose font-semibold mt-1">Replace</button>
          </div>
          <button type="button" onClick={clear} data-testid="id-upload-clear" className="text-stone-400 hover:text-red-500"><X size={18} /></button>
        </div>
      )}
      <input ref={ref} type="file" accept={ACCEPT} className="hidden" data-testid="id-upload-input"
        onChange={(e) => pick(e.target.files?.[0])} />
    </div>
  );
}
